import { useState, useEffect, useMemo } from 'react';
import { withSeasonLabel } from '@aluvo/constants/src/collectionAvailability';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth, WATCHLIST_ONBOARDING_PATH } from '../../context/AuthContext';
import { CoinCelebration } from '../../components/CoinCelebration/CoinCelebration';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import ReportQuizQuestionModal from '../../components/ReportQuizQuestionModal/ReportQuizQuestionModal';
import MissedQuestionsReview from '../../components/MissedQuestionsReview/MissedQuestionsReview';
import PendingCoinResult from './PendingCoinResult';
import styles from './QuizResult.module.css';
import { APP_CONFIG } from '../../config/appConfig';

function formatTimeRemaining(nextRetryAt) {
    if (!nextRetryAt) return null;
    const diff = new Date(nextRetryAt) - Date.now();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1000);
    if (h > 0) return `Retry available in ${h}h ${m}m`;
    if (m > 0) return `Retry available in ${m}m ${s}s`;
    return `Retry available in ${s}s`;
}

function truncate(text, maxLength = 72) {
    if (!text) return '';
    return text.length <= maxLength ? text : text.slice(0, maxLength).trimEnd() + '…';
}

function publicCoinUrl(coinId) {
    return `${APP_CONFIG.webUrl}/coin/${encodeURIComponent(coinId)}`;
}

function publicCollectionUrl(collectionId, username) {
    const base = `${APP_CONFIG.webUrl}/collection/${encodeURIComponent(collectionId)}`;
    return username ? `${base}?u=${encodeURIComponent(username)}` : base;
}

function CollectionProgressSection({ collections, onVerifyNext }) {
    return (
        <div className={styles.progressSection}>
            <p className={styles.progressSectionLabel}>⚡ GETTING CLOSER</p>
            {collections.map((col) => (
                <CollectionProgressCard
                    key={col.collectionId}
                    collection={col}
                    onVerifyNext={onVerifyNext}
                />
            ))}
        </div>
    );
}

function CollectionProgressCard({ collection, onVerifyNext }) {
    const progressPercent = Math.min(
        (collection.verifiedCount / collection.requiredCount) * 100,
        100
    );

    const verifyButtonText = collection.remaining === 1 && collection.nextTitle
        ? `Verify ${collection.nextTitle.title} to unlock ${collection.coinName} →`
        : collection.nextTitle
            ? `Verify ${collection.nextTitle.title} next →`
            : null;

    return (
        <div className={styles.progressCard}>
            <Link
                to={`/app/collections/${collection.collectionSlug || collection.collectionId}`}
                className={styles.progressCollectionName}
            >
                {collection.collectionName}
            </Link>
            <p className={styles.progressCoinName}>💎 {collection.coinName}</p>

            <div className={styles.progressBarTrack}>
                <div
                    className={styles.progressBarFill}
                    style={{ width: `${progressPercent}%` }}
                />
            </div>

            <div className={styles.progressMeta}>
                <span className={styles.progressCount}>
                    {collection.verifiedCount} / {collection.requiredCount}
                </span>
                <span className={styles.progressRemaining}>
                    ⚡ {collection.remaining} more to unlock
                </span>
            </div>

            {collection.remaining <= 2 && collection.nextTitle && verifyButtonText && (
                <button
                    className={styles.verifyNextBtn}
                    onClick={() => onVerifyNext(collection.nextTitle)}
                >
                    {verifyButtonText}
                </button>
            )}
        </div>
    );
}

function QuizReportSection({ attemptId, answeredQuestions }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    console.log('quiz report enabled:', APP_CONFIG.quizReportEnabled);

    if (!APP_CONFIG.quizReportEnabled || !answeredQuestions?.length) return null;

    function handlePickQuestion(aq) {
        setSelectedQuestion(aq);
        setPickerOpen(false);
        setReportOpen(true);
    }

    function handleCloseModal() {
        setReportOpen(false);
        setSelectedQuestion(null);
    }

    return (
        <div className={styles.reportSection}>
            <p className={styles.reportPrompt}>Something wrong with a question?</p>
            <button className={styles.reportIssueBtn} onClick={() => setPickerOpen(true)}>
                🚩 Report Question
            </button>

            {/* Question picker */}
            {pickerOpen && (
                <div className={styles.pickerOverlay} onClick={() => setPickerOpen(false)}>
                    <div className={styles.pickerContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.pickerHeader}>
                            <h3>Which question?</h3>
                            <button
                                className={styles.pickerCloseBtn}
                                onClick={() => setPickerOpen(false)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                        <p className={styles.pickerSubtitle}>Select the question you'd like to report</p>
                        <ul className={styles.pickerList}>
                            {answeredQuestions.map((aq, i) => (
                                <li key={aq.question?.id ?? i}>
                                    <button
                                        className={styles.pickerItem}
                                        onClick={() => handlePickQuestion(aq)}
                                    >
                                        <span className={styles.pickerIndex}>Q{i + 1}</span>
                                        <span className={styles.pickerSnippet}>
                                            {truncate(aq.question?.question)}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <ReportQuizQuestionModal
                isOpen={reportOpen}
                onClose={handleCloseModal}
                quizAttemptId={attemptId}
                questionId={selectedQuestion?.question?.id}
                questionText={selectedQuestion?.question?.question}
                selectedAnswer={selectedQuestion?.selectedIndex}
            />
        </div>
    );
}

export default function QuizResult() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { result, kind, seasonNumber, mediaTitle, tmdbId, attemptId, answeredQuestions } = state || {};

    const [timeRemaining, setTimeRemaining] = useState(() =>
        result?.upgradeInfo?.nextRetryAt ? formatTimeRemaining(result.upgradeInfo.nextRetryAt) : null
    );

    // ── Build ordered celebration queue ──────────────────────────────────────
    const celebrationQueue = useMemo(() => {
        if (!result?.passed) return [];

        const { badge, seriesMasteryReward, collectionRewards } = result;
        const queue = [];

        if (badge && !badge.alreadyHad) {
            const coinId = badge.coin?.shareCode ?? badge.coin?.id ?? badge.userBadgeId ?? null;
            queue.push({
                coinImageUrl: badge.coin?.coinImageUrl ?? null,
                coinLabel:    badge.badge
                    ? `${badge.badge.label_top ?? ''} · ${badge.badge.label_bottom ?? ''}`
                    : null,
                subtitle: null,
                shareUrl: coinId ? publicCoinUrl(coinId) : null,
                shareTitle: 'Share your Aluvo coin',
                shareMessage: mediaTitle
                    ? `I just earned a verified coin for ${mediaTitle} on Aluvo.`
                    : 'I just earned a verified coin on Aluvo.',
            });
        } else if (badge?.upgraded) {
            const coinId = badge.coin?.shareCode ?? badge.coin?.id ?? badge.userBadgeId ?? null;
            queue.push({
                coinImageUrl: badge.coin?.coinImageUrl ?? null,
                coinLabel:    `${badge.previousRarity} → ${badge.coin?.rarity}`,
                subtitle:     'Coin Upgraded!',
                shareUrl: coinId ? publicCoinUrl(coinId) : null,
                shareTitle: 'Share your upgraded Aluvo coin',
                shareMessage: mediaTitle
                    ? `I just upgraded my ${mediaTitle} coin on Aluvo.`
                    : 'I just upgraded a coin on Aluvo.',
            });
        }

        if (seriesMasteryReward) {
            const coinId = seriesMasteryReward.shareCode ?? seriesMasteryReward.id ?? seriesMasteryReward.userBadgeId ?? null;
            queue.push({
                coinImageUrl: seriesMasteryReward.coinImageUrl ?? null,
                coinLabel:    seriesMasteryReward.title ?? null,
                subtitle:     '⭐ Series Mastery Unlocked!',
                shareUrl: coinId ? publicCoinUrl(coinId) : null,
                shareTitle: 'Share your Series Mastery coin',
                shareMessage: seriesMasteryReward.title
                    ? `I just unlocked ${seriesMasteryReward.title} on Aluvo.`
                    : 'I just unlocked a Series Mastery coin on Aluvo.',
            });
        }

        if (collectionRewards?.length) {
            for (const reward of collectionRewards) {
                const collectionId = reward.collectionSlug ?? reward.collectionId ?? null;
                const collectionName = reward.collectionName ?? reward.rewardCoinName;
                queue.push({
                    coinImageUrl: reward.coinImageUrl ?? null,
                    // Animated Signature rewards play in the unlock screen
                    coinMedia:    reward.coinMedia ?? null,
                    viewCoinPath: collectionId ? `/collections/${collectionId}` : null,
                    coinLabel:    reward.rewardCoinName ?? collectionName ?? null,
                    subtitle:     '🏆 Collection Complete!',
                    shareUrl: collectionId ? publicCollectionUrl(collectionId, user?.username) : null,
                    shareTitle: 'Share your completed Aluvo collection',
                    shareMessage: withSeasonLabel(collectionName
                        ? `I just completed ${collectionName} on Aluvo.`
                        : 'I just completed a collection on Aluvo.', reward),
                });
            }
        }

        return queue;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [celebrationIndex, setCelebrationIndex] = useState(0);
    const [allCelebrationsDone, setAllCelebrationsDone] = useState(celebrationQueue.length === 0);

    const handleCelebrationComplete = () => {
        const next = celebrationIndex + 1;
        if (next < celebrationQueue.length) {
            setCelebrationIndex(next);
        } else {
            setAllCelebrationsDone(true);
        }
    };

    useEffect(() => {
        if (!result?.upgradeInfo?.nextRetryAt) return;
        const interval = setInterval(() => {
            setTimeRemaining(formatTimeRemaining(result.upgradeInfo.nextRetryAt));
        }, 1000);
        return () => clearInterval(interval);
    }, [result?.upgradeInfo?.nextRetryAt]);

    if (!result) {
        return (
            <div className={styles.container}>
                <h1>No challenge result found</h1>
                <button onClick={() => navigate('/')}>Go Home</button>
            </div>
        );
    }

    const { passed, score, totalQuestions, requiredToPass, badge, upgradeInfo, seriesMasteryReward, collectionRewards, collectionProgress } = result;

    // Trusted server flag: this submission created the user's first coin. The
    // post-activation Watchlist step follows the result, never before it.
    const isFirstCoin = result.isFirstCoin === true && !!user;
    const goToWatchlistOnboarding = () => {
        // replace: back from the Watchlist step never lands on this result.
        navigate(WATCHLIST_ONBOARDING_PATH, {
            replace: true,
            state: { entry: 'first_coin', excludeTmdbId: tmdbId ?? null, excludeKind: kind ?? null },
        });
    };

    const seasonSuffix = kind === 'tv' && seasonNumber != null ? ` — Season ${seasonNumber}` : '';
    const fullTitle = mediaTitle ? `${mediaTitle}${seasonSuffix}` : null;
    const coinLabel = badge && !badge.alreadyHad
        ? `${badge.badge?.label_top ?? ''} · ${badge.badge?.label_bottom ?? ''}`
        : null;
    const canRetryNow = upgradeInfo?.canRetry;
    const currentRarity = upgradeInfo?.currentRarity;
    // After score, coin and progression; before next actions.
    const missedReview = (
        <MissedQuestionsReview
            attemptId={user ? (attemptId ?? result.attemptId ?? null) : null}
            score={score}
            totalQuestions={totalQuestions}
            mediaType={kind}
            tmdbId={tmdbId}
            seasonNumber={seasonNumber}
            tierResult={passed ? (badge?.coin?.rarity ?? null) : null}
        />
    );
    // First quiz before email verification: scored, coin held until verified.
    if (result.rewardDeferred) {
        return (
            <PendingCoinResult
                result={result}
                fullTitle={fullTitle}
                missedReview={missedReview}
                reportSection={<QuizReportSection attemptId={attemptId} answeredQuestions={answeredQuestions} />}
            />
        );
    }

    const showUpgradeHint = upgradeInfo && currentRarity !== 'gold' && currentRarity !== 'legendary';
    const showLegendaryHint = upgradeInfo && currentRarity === 'gold';

    if (passed) {
        const currentCelebration = celebrationQueue[celebrationIndex];
        const showCelebration = !allCelebrationsDone && currentCelebration != null;

        const coinEarned = badge && (!badge.alreadyHad || badge.upgraded);
        const showProgressSection =
            collectionProgress?.length > 0 &&
            allCelebrationsDone &&
            coinEarned;

        const progressToShow = collectionProgress
            ?.slice()
            .sort((a, b) => a.remaining - b.remaining)
            .slice(0, 3);

        return (
            <div className={styles.page}>
                {showCelebration && (
                    <CoinCelebration
                        key={celebrationIndex}
                        coinLabel={currentCelebration.coinLabel}
                        coinImageUrl={currentCelebration.coinImageUrl}
                        coinMedia={currentCelebration.coinMedia}
                        onViewCoin={currentCelebration.coinMedia?.type === 'ANIMATED' && currentCelebration.viewCoinPath
                            ? () => navigate(currentCelebration.viewCoinPath)
                            : undefined}
                        subtitle={currentCelebration.subtitle}
                        shareTitle={currentCelebration.shareTitle}
                        shareMessage={currentCelebration.shareMessage}
                        shareUrl={currentCelebration.shareUrl}
                        onComplete={handleCelebrationComplete}
                    />
                )}

                <div className={styles.inner} style={{ opacity: allCelebrationsDone ? 1 : 0, transition: 'opacity 0.3s ease' }}>
                    <span className={styles.emoji}>🎉</span>
                    <h1 className={styles.headline}>Verified!</h1>
                    {fullTitle && <p className={styles.subtitle}>{fullTitle}</p>}
                    <p className={styles.score}>{score} / {totalQuestions} correct</p>

                    {coinLabel && (
                        <div className={styles.coinBox}>
                            <span className={styles.coinIcon}>🪙</span>
                            <div>
                                <p className={styles.coinEarnedLabel}>Coin earned</p>
                                <p className={styles.coinName}>{coinLabel}</p>
                            </div>
                        </div>
                    )}

                    {badge?.upgraded && (
                        <div className={`${styles.coinBox} ${styles.coinUpgraded}`}>
                            <span className={styles.coinIcon}>✨</span>
                            <div>
                                <p className={styles.coinEarnedLabel}>Coin upgraded!</p>
                                <p className={styles.coinName}>{badge.previousRarity} → {badge.coin?.rarity}</p>
                            </div>
                        </div>
                    )}

                    {badge?.alreadyHad && !badge?.upgraded && (
                        <div className={`${styles.coinBox} ${styles.coinAlready}`}>
                            <span className={styles.coinIcon}>🪙</span>
                            <p className={styles.alreadyHadText}>You already have this {badge.coin?.rarity} coin.</p>
                        </div>
                    )}

                    {/* Series Mastery summary row */}
                    {seriesMasteryReward && (
                        <div className={styles.coinBox} style={{ borderColor: '#FACC15', background: 'rgba(250,204,21,0.08)' }}>
                            <span className={styles.coinIcon}>⭐</span>
                            <div>
                                <p className={styles.coinEarnedLabel}>Series Mastery unlocked!</p>
                                <p className={styles.coinName} style={{ color: '#FACC15' }}>{seriesMasteryReward.title}</p>
                            </div>
                        </div>
                    )}

                    {/* Collection reward summary rows */}
                    {collectionRewards?.length > 0 && collectionRewards.map((reward) => (
                        <div
                            key={reward.collectionId}
                            className={styles.coinBox}
                            style={{ borderColor: '#A78BFA', background: 'rgba(167,139,250,0.08)' }}
                        >
                            <span className={styles.coinIcon}>🏆</span>
                            <div>
                                <p className={styles.coinEarnedLabel}>Collection complete!</p>
                                <p className={styles.coinName} style={{ color: '#A78BFA' }}>
                                    {reward.collectionName ?? reward.rewardCoinName}
                                </p>
                            </div>
                        </div>
                    ))}

                    {/* In-progress collections advanced this quiz */}
                    {showProgressSection && (
                        <CollectionProgressSection
                            collections={progressToShow}
                            onVerifyNext={(title) => navigate(`/movie/${title.media_type}/${title.tmdb_id}`)}
                        />
                    )}

                    {showUpgradeHint && (
                        <div className={styles.hintBox}>
                            <p className={styles.hintText}>
                                {canRetryNow
                                    ? '💡 You can retry to upgrade your coin to a higher rarity!'
                                    : timeRemaining
                                        ? `⏱️ ${timeRemaining}`
                                        : '💡 You can retry in 24 hours to upgrade your coin'}
                            </p>
                        </div>
                    )}

                    {showLegendaryHint && (
                        <div className={styles.hintBox}>
                            <p className={styles.hintText}>
                                {canRetryNow
                                    ? '💎 Ready for Legendary! Score perfect to unlock the highest tier.'
                                    : upgradeInfo?.nextRetryAt
                                        ? `⏱️ Legendary attempt unlocks in: ${timeRemaining || '...'}`
                                        : '💎 Score perfect on your next attempt to unlock Legendary!'}
                            </p>
                        </div>
                    )}

                    {missedReview}

                    <AppPromotionCard
                        variant={badge?.coin?.rarity === 'gold' ? 'post_gold' : 'inline'}
                        compact
                    />

                    <div className={styles.actions}>
                        {isFirstCoin ? (
                            <button type="button" className={styles.btnPrimary} onClick={goToWatchlistOnboarding}>
                                Continue
                            </button>
                        ) : (
                            <>
                                {user ? (
                                    <Link to="/me" className={styles.btnPrimary}>View My Profile</Link>
                                ) : (
                                    <Link to="/register" className={styles.btnPrimary}>Create Account to Save Coins</Link>
                                )}
                                <Link to="/discover" className={styles.btnSecondary}>Keep Exploring</Link>
                            </>
                        )}
                    </div>

                    <QuizReportSection attemptId={attemptId} answeredQuestions={answeredQuestions} />
                </div>
            </div>
        );
    }

    // Failed
    const needed = requiredToPass ?? 5;
    const missed = Math.max(0, needed - score);

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <span className={styles.emoji}>😅</span>
                <h1 className={styles.headline}>Not quite!</h1>
                {fullTitle && <p className={styles.subtitle}>{fullTitle}</p>}
                <p className={styles.score}>{score} / {totalQuestions} correct</p>

                <div className={styles.hintBox}>
                    <p className={styles.hintText}>
                        You needed {needed} to pass.{' '}
                        {missed === 1
                            ? 'Just 1 more and you would have earned the coin!'
                            : `${missed} more correct and you would have earned it!`}
                    </p>
                </div>

                {upgradeInfo && (
                    <div className={styles.hintBox}>
                        <p className={styles.hintText}>
                            {canRetryNow
                                ? '💡 You can retry to earn a coin!'
                                : timeRemaining || '💡 You can retry in 24 hours'}
                        </p>
                    </div>
                )}

                {missedReview}

                <div className={styles.actions}>
                    <Link to="/discover" className={styles.btnPrimary}>Try Another Title</Link>
                    <Link to="/" className={styles.btnSecondary}>Back to Home</Link>
                </div>

                <QuizReportSection attemptId={attemptId} answeredQuestions={answeredQuestions} />
            </div>
        </div>
    );
}
